// Site footer chrome. eb-ui rendered <SiteStructure> which wrapped a <SiteFooter>;
// the sanctioned Valkyrie footer is @eventbrite/legacy-infra.site-footer, fed by
// getGlobalFooterServerSideProps (see the page's getServerSideProps). Modeled on the
// sibling homepage-webapp `src/components/Footer/Footer.tsx`; locale change uses plain
// window.location (avoids pulling @eventbrite/legacy-infra.http just for setWindowLocation).

import React from 'react';
import { SiteFooter } from '@eventbrite/legacy-infra.site-footer';
import type { GlobalFooterProps } from '@eventbrite/legacy-infra.site-footer';
import { LOCALE_DEFAULTS } from '@eventbrite/webapp-locales';
import { useIntl } from 'react-intl';

export interface FooterProps {
    serverUrl: string;
    // The CoreEnv.localeInfo shape SiteFooter expects (contact/social/help fields).
    // Built from webapp-locales getDefaults() — see src/lib/localeInfo.ts.
    localeInfo: Record<string, unknown>;
    isUserAuthenticated: boolean;
    globalFooterProps: GlobalFooterProps;
}

export function Footer({
    serverUrl,
    localeInfo,
    isUserAuthenticated,
    globalFooterProps,
}: FooterProps) {
    const { showMinimalLinks, thirdFooterColumn = [], popularSearches, eventsToPlan, impressumHelpLink } =
        globalFooterProps;

    const intl = useIntl();

    const locales = LOCALE_DEFAULTS.map(({ locale, localized_language_name, locale_name }) => ({
        locale,
        localized_language_name,
        locale_name,
    }));
    const localesSorted = locales.slice().sort((a, b) => a.locale_name.localeCompare(b.locale_name));

    const trimmedServerUrl = serverUrl.replace(/\/$/, '');

    const handleLocaleChange = (locale: string) => {
        if (typeof window !== 'undefined') {
            window.location.assign(
                `${trimmedServerUrl}/international/change_locale/?locale=${locale}`,
            );
        }
    };

    return (
        <div data-testid="footer-section">
            <SiteFooter
                serverUrl={trimmedServerUrl}
                showMinimalLinks={showMinimalLinks}
                showMarketingLinks={!isUserAuthenticated}
                locales={localesSorted}
                localeInfo={localeInfo}
                onLocaleChange={handleLocaleChange}
                thirdFooterColumn={thirdFooterColumn || []}
                thirdFooterColumnName={intl.formatMessage({
                    id: 'Find Events',
                    defaultMessage: 'Find Events',
                })}
                popularSearches={popularSearches}
                eventsToPlan={eventsToPlan}
                helpLinks={{ impressum: impressumHelpLink }}
            />
        </div>
    );
}

export default Footer;
